import { create } from "zustand";
import {
  MakeReportProps,
  PartialProps,
  ReportState,
  SendFeedbackProps,
} from "../types/api";
import { devtools } from "zustand/middleware";
import {
  buildReport,
  getReport,
  getReportFeedback,
  sendReportFeedback,
  getReportStatus,
  getReportTranslateStatus,
  getAllReports,
  removeReport,
  partialBuildReport,
  generateNewReport,
  changeUiPreferences,
  fetchUserPrice,
  processUserPrice,
} from "../api/reportApi";
import useProfileStore from "./useProfileStore";

const loadFromLocalStorage = <T>(key: string, defaultValue: T) => {
  const saved = localStorage.getItem(key);
  return saved ? JSON.parse(saved) : defaultValue;
};

const useReportStore = create<ReportState>()(
  devtools(
    (set, get) => ({
      report: null,
      reportLoading: false,
      reportProcessing: loadFromLocalStorage("reportProcessing", null),
      reportId: loadFromLocalStorage("reportId", null),
      error: loadFromLocalStorage("reportError", null),
      feedbacks: [],
      progress: null,
      allReports: [],
      userMarketPrice: undefined,

      fetchAllReports: async (lan: string = "en") => {
        set({ reportLoading: true, error: null });
        try {
          const response = await getAllReports(lan);

          if (!response) {
            throw new Error("Failed to fetch reports");
          }

          const reports = response.data;
          if (reports.length === 0) {
            set({ error: "not_found", reportLoading: false });
            return;
          }

          const currentReportId = get().reportId;

          const firstReport = reports[0];
          const isEnglish = lan === "en";

          if (!currentReportId) {
            const newReportId = isEnglish
              ? firstReport.id
              : firstReport.original_report ?? undefined;

            if (newReportId !== undefined) {
              localStorage.setItem("reportId", JSON.stringify(newReportId));
              set({ reportId: newReportId });
            }
          }

          set({
            allReports: reports,
            reportLoading: false,
          });
          return reports;
        } catch (error: any) {
          if (error?.response?.status === 404) {
            set({ error: "not_found", reportLoading: false });
          } else {
            set({
              error: error?.response?.data?.message ?? "Unknown error",
              reportLoading: false,
            });
          }
          throw error;
        }
      },

      fetchReport: async (reportId: number, language: string = "en") => {
        set({ reportLoading: true, error: null });
        try {
          const response = await getReport(reportId, language);
          if (!response) {
            throw new Error("Failed to fetch report");
          }
          set({
            report: response.data,
            reportLoading: false,
          });
        } catch (error: any) {
          if (error.response.status === 404) {
            if (get().allReports.length > 0) {
              set({
                reportId:
                  get().allReports[0].original_report !== null
                    ? get().allReports[0].original_report!
                    : get().allReports[0].id,
                reportLoading: false,
              });
              localStorage.setItem(
                "reportId",
                JSON.stringify(
                  get().allReports[0].original_report !== null
                    ? get().allReports[0].original_report!
                    : get().allReports[0].id
                )
              );
            } else {
              set({ error: "not_found", reportLoading: false });
            }
          } else {
            set({ error: error.response.data.message, reportLoading: false });
          }
        }
      },

      makeReport: async () => {
        try {
          const response = await buildReport();
          if (!response) {
            throw new Error("Failed to build report");
          }

          localStorage.setItem(
            "reportId",
            JSON.stringify(response.data.report_id)
          );
          localStorage.setItem(
            "reportProcessing",
            JSON.stringify("processing")
          );
          localStorage.removeItem("reportError");

          set({
            error: null,
            reportProcessing: "processing",
            reportId: response.data.report_id,
            userMarketPrice: undefined,
          });
          return true;
        } catch (error: any) {
          if (error?.status === 400) {
            throw error;
          } else {
            localStorage.setItem("reportError", JSON.stringify(error.message));

            set({ error: error.message, reportLoading: false });
          }

          return false;
        }
      },

      generateReport: async (credential: MakeReportProps) => {
        try {
          const response = await generateNewReport(credential);
          if (!response) {
            throw new Error("Failed to build report");
          }

          localStorage.setItem(
            "reportId",
            JSON.stringify(response.data.report_id)
          );
          localStorage.setItem(
            "reportProcessing",
            JSON.stringify("processing")
          );
          localStorage.removeItem("reportError");

          set({
            error: null,
            reportProcessing: "processing",
            reportId: response.data.report_id,
          });
          return true;
        } catch (error: any) {
          if (error?.status === 400) {
            throw error;
          } else {
            localStorage.setItem("reportError", JSON.stringify(error.message));

            set({ error: error.message, reportLoading: false });
          }

          return false;
        }
      },

      makePartialReport: async (credential: PartialProps) => {
        try {
          const response = await partialBuildReport(credential);

          if (!response) {
            throw new Error("Failed to build report");
          }

          localStorage.setItem(
            "reportId",
            JSON.stringify(response.data.report_id)
          );
          localStorage.setItem(
            "reportProcessing",
            JSON.stringify("processing")
          );
          localStorage.removeItem("reportError");

          set({
            error: null,
            reportProcessing: "processing",
            reportId: response.data.report_id,
          });
          return true;
        } catch (error: any) {
          if (error?.status === 400) {
            throw error;
          } else {
            localStorage.setItem("reportError", JSON.stringify(error.message));

            set({ error: error.message, reportLoading: false });
          }

          return false;
        }
      },

      fetchReportStatus: async (reportId: number) => {
        try {
          const statusResponse = await getReportStatus(reportId);
          if (!statusResponse) {
            throw new Error("Failed to fetch report status");
          }
          localStorage.setItem(
            "reportProcessing",
            JSON.stringify(statusResponse.data.status)
          );
          set({
            progress: statusResponse.data.progress,
            reportProcessing: statusResponse.data.status,
          });
          return statusResponse;
        } catch (error: any) {
          if (error.response.status === 404 || error.response.status === 500) {
            set({ error: "not_found", reportLoading: false });
          } else {
            set({ error: error.response.data.message, reportLoading: false });
          }
        }
      },

      fetchReportTranslateStatus: async (language: string, id: number) => {
        try {
          const statusResponse = await getReportTranslateStatus(language, id);

          return statusResponse;
        } catch (error: any) {
          throw error;
        }
      },

      fetchReportFeedback: async (report_id: number) => {
        try {
          const response = await getReportFeedback(report_id);
          if (!response) {
            throw new Error("Failed to fetch feedback");
          }
          set({
            feedbacks: response.data,
          });
        } catch (error: any) {
          throw error;
        }
      },
      deleteReport: async (report_id: number) => {
        try {
          await removeReport(report_id);
          const currentLanguage = useProfileStore.getState().profileLanguage;
          const isEnglish = Object.keys(currentLanguage!)[0] === "en";

          const filterReports = (
            reports: any[],
            idField: "id" | "original_report"
          ) => reports.filter((r) => r[idField] !== report_id);

          if (!isEnglish) {
            const langKey = Object.keys(currentLanguage!)[0];
            const response = await getAllReports(langKey);
            const remainingReports = response.data;

            if (remainingReports.length === 0) {
              set({ error: "not_found", reportId: undefined });
              localStorage.removeItem("reportId");
            } else {
              const nextReportId =
                remainingReports[0].original_report ?? undefined;
              set({ reportId: nextReportId });
              localStorage.setItem("reportId", String(nextReportId));
            }

            set((state) => ({
              allReports: filterReports(state.allReports, "original_report"),
            }));
          } else {
            set((state) => {
              const remainingReports = filterReports(state.allReports, "id");

              if (remainingReports.length === 0) {
                localStorage.removeItem("reportId");
                return {
                  allReports: remainingReports,
                  reportId: undefined,
                  error: "not_found",
                };
              }

              const nextReportId = remainingReports[0].id;
              localStorage.setItem("reportId", String(nextReportId));

              return {
                allReports: remainingReports,
                reportId: nextReportId,
              };
            });
          }

          return true;
        } catch (error: any) {
          throw error;
        }
      },

      sendReportFeedback: async ({
        report_id,
        section,
        rating,
        thoughts,
      }: SendFeedbackProps) => {
        try {
          const { data } = await sendReportFeedback({
            report_id,
            section,
            rating,
            thoughts,
          });

          set((state) => ({
            feedbacks: [
              ...state.feedbacks,
              {
                id: data.id,
                report: data.report,
                section: data.section,
                result_assessment: data.result_assessment,
                thoughts: data.thoughts,
                created_at: data.created_at,
              },
            ],
          }));
        } catch (error: any) {
          throw error;
        }
      },

      updatePreferences: async (id: number, val: boolean) => {
        try {
          const response = await changeUiPreferences(id, val);
          if (!response) {
            throw new Error("Failed to fetch feedback");
          }
          set({
            report: {
              ...get().report,
              ui_preferences: response.data,
            },
          });
        } catch (error: any) {
          throw error;
        }
      },

      getUserPrice: async (lan: string) => {
        try {
          const response = await fetchUserPrice(lan);
          if (!response) {
            throw new Error("Failed to fetch user price");
          }
          set({
            userMarketPrice: response.data,
          });
        } catch (error: any) {
          console.log(error);
        }
      },

      getFeedbacksBySection: (section: string) => {
        return (get().feedbacks || []).filter(
          (feedback) => feedback.section === section
        );
      },

      resetReportError: () => {
        set({
          error: null,
        });
      },

      setReportId: (id: number) => {
        localStorage.setItem("reportId", JSON.stringify(id));
        set({
          reportId: id,
        });
      },

      resetReportState: () => {
        set({
          report: undefined,
          reportId: undefined,
          progress: undefined,
          reportProcessing: undefined,
        });
      },
    }),
    { name: "reportStore" }
  )
);

export default useReportStore;
